/* eslint-disable class-methods-use-this */
import {
  CreatedEvent,
  DefaultApi,
  JsContractEntryOneOf,
  JsGetActiveContractsResponse,
  JsGetUpdatesResponse,
  Transaction,
} from '@digitalasset/json-api-ts-client';
import { WSSupport } from '../WSSupport';
import { ACSCacheUtils } from './ACSCacheUtils';
import { ACS_UPDATE_CONFIG } from './ACSConfig';
import { ACSContainerHttpUtils } from './ACSContainerHttpUtils';
import { ACSContainerLogger } from './ACSContainerLogger';
import { ACSKeyData, ACSSet, InitializedACSSet, SerializedACSContainer } from './ACSTypes';

// As in current implementation we re-initialize ACSContainer after every query
// we could just use a single function for updating an ACS Set. Though class approach is
// convenient for easy re-call of update inside single ACS Container
export class ACSContainer {
  public static useLogs: boolean = true;

  // Left in UPPER CASE for better readability
  readonly LOG: ACSContainerLogger | null;

  private acsSet: ACSSet;

  constructor(
    private readonly keyData: ACSKeyData,
    serializedContainer: SerializedACSContainer | null = null,
  ) {
    this.LOG = ACSContainer.useLogs ? new ACSContainerLogger() : null;

    if (serializedContainer && this.LOG) {
      this.LOG.initializedFromPreviousContainerInstance(serializedContainer);
    }

    if (serializedContainer) {
      this.acsSet = { ...serializedContainer };
    } else {
      this.acsSet = {
        initialized: false,
        key: ACSCacheUtils.keyDataToString(keyData),
      };
    }
  }

  async update(
    targetOffset: number,
    api: DefaultApi,
    wsSupport: WSSupport,
  ): Promise<JsGetActiveContractsResponse[]> {
    if (!this.acsSet.initialized) {
      this.acsSet = await ACSContainerHttpUtils.readACS(
        targetOffset,
        this.keyData,
        api,
        wsSupport,
        ACS_UPDATE_CONFIG,
        this.LOG,
      );

      this.LOG?.fetchedEntireACS(this.acsSet);

      return this.acsSet.acs;
    }

    const isTargetOffsetLowerThanCurrentOffset = this.acsSet.acsOffset > targetOffset;

    if (isTargetOffsetLowerThanCurrentOffset) {
      // This is not something that should happen on any occasion, so an error should point as in the direction
      // that something with the code is fundamentally broken and should be fixed.
      this.LOG?.triedToFetchUpdatesWithLowerOffset(this.acsSet.acsOffset, targetOffset);
      return Promise.reject(new Error(`Next offset cannot be lower than current offset!`));
    }

    const updates = await ACSContainerHttpUtils.readUpdates(
      this.acsSet.acsOffset,
      targetOffset,
      ACSContainerHttpUtils.createEventFormat(this.keyData),
      api,
      ACS_UPDATE_CONFIG,
      this.LOG,
    );

    this.LOG?.fetchedACSUpdates(
      updates,
      this.acsSet.acsOffset,
      targetOffset,
      ACS_UPDATE_CONFIG.maxUpdatesToFetch,
    );

    this.acsSet = this.createNewACSSet(this.acsSet.acs, this.acsSet.acsOffset, updates);
    this.LOG?.updatedACSSet(this.acsSet);

    // in practise length should never be > maxUpdatesToFetch only equal (server should never return more than limit in query). This is just a safeguard.
    if (updates.length >= ACS_UPDATE_CONFIG.maxUpdatesToFetch) {
      // recall this to get more updates
      this.LOG?.willTryToFetchMoreUpdates();
      return this.update(targetOffset, api, wsSupport);
    }

    return this.acsSet.acs;
  }

  public serialize(): SerializedACSContainer {
    return {
      ...this.acsSet,
      // version: ACS_CONFIG.version,
      log: this.LOG?.log,
    };
  }

  private createNewACSSet(
    acs: JsGetActiveContractsResponse[],
    acsOffset: number,
    updates: JsGetUpdatesResponse[],
  ): InitializedACSSet {
    const acsKeyValuePairs = acs.map<[string, JsGetActiveContractsResponse]>((c) => {
      return [
        (c.contractEntry as JsContractEntryOneOf).JsActiveContract.createdEvent.contractId,
        c,
      ];
    });

    const acsMap = new Map<string, JsGetActiveContractsResponse>(acsKeyValuePairs);
    let newAcsOffset = acsOffset;

    // We assume that all the received updates are in order (from the oldest offset to latest)
    updates.forEach(({ update }) => {
      if ('Transaction' in update) {
        const transaction = update.Transaction;

        transaction.value.events?.forEach((event) => {
          if ('CreatedEvent' in event) {
            const { contractId: eventContractId, offset: eventOffset } = event.CreatedEvent;

            if (eventOffset <= acsOffset) {
              // Maybe it we got not-monotonic data we should just throw an error and re-fetch all?
              this.LOG?.encounteredOlderEvent(
                `While processing contract addition`,
                acsOffset,
                eventOffset,
                event,
              );
              return;
            }

            const eventActiveContract = this.eventToActiveContract(transaction, event.CreatedEvent);

            acsMap.set(eventContractId, eventActiveContract);
            newAcsOffset = eventOffset;

            this.LOG?.addedContractToACSSet(eventContractId, eventOffset);
          } else if ('ArchivedEvent' in event) {
            const { contractId: eventContractId, offset: eventOffset } = event.ArchivedEvent;

            if (eventOffset <= acsOffset) {
              this.LOG?.encounteredOlderEvent(
                `While processing contract removal`,
                acsOffset,
                eventOffset,
                event,
              );
              return;
            }

            acsMap.delete(eventContractId);
            newAcsOffset = eventOffset;

            this.LOG?.removedContractFromACSSet(eventContractId, eventOffset);
          }
        });
      } else if ('OffsetCheckpoint' in update) {
        if (update.OffsetCheckpoint.value.offset <= acsOffset) {
          return;
        }

        newAcsOffset = update.OffsetCheckpoint.value.offset;
      } else {
        this.LOG?.encounteredUpdateOfUnknownType(update);
      }
    });

    return {
      key: this.acsSet.key,
      initialized: true,
      creationTimestampMs: Date.now(),
      acs: Array.from(acsMap.values()),
      acsOffset: newAcsOffset,
    };
  }

  private eventToActiveContract(
    transaction: Transaction,
    event: CreatedEvent,
  ): JsGetActiveContractsResponse {
    return {
      workflowId: transaction.value.workflowId,
      contractEntry: {
        JsActiveContract: {
          createdEvent: event,
          synchronizerId: transaction.value.synchronizerId,
          reassignmentCounter: 0,
        },
      },
    };
  }
}
